import { useForm } from 'react-hook-form'
import {
	type SchedulersGetSchedulerProfile200,
	useBookingRequestsPublicCreateBookingRequest,
} from '@cocoonspace/api'
import { Card } from '@cocoonspace/ui/components/card'
import { Form } from '@cocoonspace/ui/components/form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useLingui } from '@lingui/react/macro'
import { isAxiosError } from 'axios'
import { toast } from 'sonner'
import * as z from 'zod'

import { useIsRouterLoading } from '~/hooks/use-is-router-loading.hook'
import { useRouter } from '~/hooks/use-localized-navigation.hook'
import { AnswerProfileHeader } from './answer-profile-header'
import { DatePicker } from './date_picker'
import { EventRecap } from './event_recap'
import { NotFound } from './not-found'
import { AnswerCardProvider, useAnswerCard } from './provider'
import { TimePicker } from './time_picker'
import { useCachedEventTypeAvailabilities } from './use-cached-event-type-availabilities.hook'
import { Validation } from './validation'

export type BookingRequestAnswerFormData = z.infer<typeof formSchema>

const formSchema = z.object({
	step: z.enum(['dateTime', 'validation']),
	title: z.string().min(1),
	day: z.string().min(1),
	start: z.string().min(1),
	email: z.email().min(1),
	requestedLocation: z.object({
		type: z.enum(['cocoon', 'custom']),
		space_id: z.string().min(1),
	}),
	additionalInfos: z.object({
		first_name: z.string().min(1),
		last_name: z.string().min(1),
		comment: z.string().optional(),
	}),
})

const StepDateTime = ({
	profile,
	embedded,
}: {
	profile?: SchedulersGetSchedulerProfile200
	embedded?: boolean
}) => {
	const { eventType, currentMonth } = useAnswerCard()
	const { t } = useLingui()

	const { data: monthSlots, isLoading } = useCachedEventTypeAvailabilities(
		eventType.id,
		{
			month: (currentMonth.getMonth() + 1).toString(),
			year: currentMonth.getFullYear().toString(),
		},
	)

	return (
		<Card className='w-full overflow-hidden shadow-lg'>
			<AnswerProfileHeader
				eventType={eventType}
				profile={profile}
			/>

			<div className='grid md:grid-cols-[minmax(0,320px)_minmax(0,1fr)]'>
				<EventRecap />

				<section className='min-w-0 border-t p-6 md:border-t-0 md:border-l md:p-8'>
					<h2 className='mb-5 text-xl font-bold'>
						{t`Choisissez une date et un horaire`}
					</h2>

					<div className='grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,340px)]'>
						<DatePicker
							monthSlots={monthSlots}
							isLoading={isLoading}
						/>

						<TimePicker
							monthSlots={monthSlots}
							embedded={embedded}
						/>
					</div>
				</section>
			</div>
		</Card>
	)
}

const StepValidation = ({
	profile,
}: {
	profile?: SchedulersGetSchedulerProfile200
}) => {
	const { eventType } = useAnswerCard()

	return (
		<Card
			variant='outline'
			className='w-full overflow-hidden shadow-lg'
		>
			<AnswerProfileHeader
				eventType={eventType}
				profile={profile}
			/>
			<div className='grid md:grid-cols-[minmax(0,320px)_minmax(0,1fr)]'>
				<EventRecap />
				<Validation />
			</div>
		</Card>
	)
}

export const AnswerCardContent = ({
	idOrSlug,
	successPath = '/scheduling/success',
	profile,
	embedded,
}: {
	idOrSlug: string
	successPath?: string
	profile?: SchedulersGetSchedulerProfile200
	embedded?: boolean
}) => {
	const router = useRouter()
	const isRouterLoading = useIsRouterLoading()
	const { step, eventType } = useAnswerCard()
	const { t } = useLingui()
	const createBookingRequestMutation =
		useBookingRequestsPublicCreateBookingRequest()

	const form = useForm<BookingRequestAnswerFormData>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			step: 'dateTime',
			title: eventType.title,
			day: '',
			start: '',
			email: '',
			requestedLocation: {
				type: eventType.location?.type as any,
				space_id: '',
			},
			additionalInfos: {
				comment: undefined,
			},
		},
	})

	// oxlint-disable-next-line no-unused-vars
	const onSubmit = ({ step, ...data }: BookingRequestAnswerFormData) => {
		return createBookingRequestMutation
			.mutateAsync({
				eventTypeId: idOrSlug,
				data,
			})
			.then((br) => {
				router.push(`${successPath}/${br.id}`)
			})
			.catch((err) => {
				if (isAxiosError(err) && err.status === 409) {
					return toast.error(t`Il n'y a pas de disponibilité pour cette date`)
				}
				return toast.error(t`Une erreur est survenue`)
			})
	}

	if (!eventType.enabled) return <NotFound />

	return (
		<Form {...form}>
			<form
				className='w-full'
				onSubmit={form.handleSubmit(onSubmit)}
			>
				<fieldset
					disabled={createBookingRequestMutation.isPending || isRouterLoading}
					className='w-full'
				>
					{step === 'dateTime' && (
						<StepDateTime
							profile={profile}
							embedded={embedded}
						/>
					)}

					{step === 'validation' && <StepValidation profile={profile} />}
				</fieldset>
			</form>
		</Form>
	)
}

export const AnswerCard = ({
	idOrSlug,
	successPath,
	profile,
	embedded,
}: {
	idOrSlug: string
	successPath?: string
	profile?: SchedulersGetSchedulerProfile200
	embedded?: boolean
}) => (
	<AnswerCardProvider idOrSlug={idOrSlug}>
		<AnswerCardContent
			idOrSlug={idOrSlug}
			successPath={successPath}
			profile={profile}
			embedded={embedded}
		/>
	</AnswerCardProvider>
)
